import React, { useMemo } from 'react';
import { Select, Transfer, Typography, Space } from 'antd';
import { useTranslation } from 'react-i18next';

const { Option } = Select;
const { Title } = Typography;

type TransferItem = {
  key: string;
  title: string;
  description?: string;
};

type AdminAccountManagementFormProps = {
  formProps: any;
  adminUsers: any[];
  availableAccounts: any[];
  selectedUserId: number | null;
  setSelectedUserId: (id: number | null) => void;
};

const AdminAccountManagementForm: React.FC<AdminAccountManagementFormProps> = ({
  formProps,
  adminUsers,
  availableAccounts,
  selectedUserId,
  setSelectedUserId,
}) => {
  const { t } = useTranslation();
  const { reset, setValue, watch } = formProps;
  const currentValues = watch();

  const filterDataSource = useMemo(() => {
    return availableAccounts.map(account => ({
      key: String(account.id),
      title: `${account.name} (ID: ${account.id})`,
      description: `Company: ${account.company_id} | Type: ${account.account_type}`,
    }));
  }, [availableAccounts]);

  const filterOption = (inputValue: string, item: TransferItem) => {
    const search = inputValue.toLowerCase();
    return !!(
      item.title.toLowerCase().includes(search) || item.description?.toLowerCase().includes(search)
    );
  };

  const selectedUser = adminUsers.find(u => u.id === selectedUserId) || null;

  const handleUserSelect = (userId: number) => {
    const user = adminUsers.find(u => u.id === userId);
    if (user) {
      setSelectedUserId(userId);

      // Use account assignments from the user data
      const assignedAccountIds = user.account_assignments.map((acc: any) => String(acc.account_id));
      reset({ targetKeys: assignedAccountIds });
    }
  };

  const handleTransferChange = (newTargetKeys: React.Key[]) => {
    setValue(
      'targetKeys',
      newTargetKeys.map(k => String(k)),
      { shouldDirty: true }
    );
  };

  return (
    <div className="admin-account-management">
      <div className="admin-account-management__header">
        <Title level={2}>{t('adminAccountManagement:title')}</Title>
        <p className="admin-account-management__description">
          {t('adminAccountManagement:description')}
        </p>
      </div>

      <div className="admin-account-management__content">
        <Space direction="vertical" size="large" className="w-full max-w-4xl">
          <div className="admin-account-management__user-select">
            <label className="block mb-2 font-medium">
              {t('adminAccountManagement:selectUser')}
            </label>
            <Select
              className="w-full"
              placeholder={t('adminAccountManagement:selectUserPlaceholder')}
              value={selectedUserId}
              onChange={handleUserSelect}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) => {
                const user = adminUsers.find(u => u.id === option?.value);
                if (!user) return false;
                const searchLower = input.toLowerCase();
                return (
                  user.email.toLowerCase().includes(searchLower) ||
                  user.first_name.toLowerCase().includes(searchLower) ||
                  user.last_name.toLowerCase().includes(searchLower)
                );
              }}
            >
              {adminUsers.map(user => (
                <Option key={user.id} value={user.id}>
                  <div>
                    <strong>{user.email}</strong>
                    <div className="text-xs text-gray-600">
                      {`${user.first_name} ${user.last_name} • ${user.account_assignments.length} account(s)`}
                    </div>
                  </div>
                </Option>
              ))}
            </Select>
          </div>

          {selectedUser ? (
            <>
              <div className="admin-account-management__selected-user">
                <Title level={4} className="mb-1">
                  {selectedUser.email}
                </Title>
                <p className="text-gray-600 mb-0">
                  {selectedUser.first_name} {selectedUser.last_name}
                </p>
              </div>

              <Transfer
                dataSource={filterDataSource}
                targetKeys={currentValues.targetKeys}
                onChange={handleTransferChange}
                render={item => item.title}
                showSearch
                filterOption={filterOption}
                titles={[
                  t('adminAccountManagement:availableAccounts'),
                  t('adminAccountManagement:assignedAccounts'),
                ]}
                locale={{
                  itemUnit: t('adminAccountManagement:account'),
                  itemsUnit: t('adminAccountManagement:accounts'),
                  searchPlaceholder: t('adminAccountManagement:searchAccount'),
                }}
              />
            </>
          ) : (
            <div className="admin-account-management__no-selection">
              <p>{t('adminAccountManagement:noUserSelected')}</p>
            </div>
          )}
        </Space>
      </div>
    </div>
  );
};

export default AdminAccountManagementForm;
